import { useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import * as authService from '@/services/authService';
import type { LoginRequest, RegisterRequest } from '@/types/auth';

/**
 * Customer authentication hook providing login, register, logout, and OAuth
 * flow. It reads and writes the customer session only; the admin session is
 * managed separately (see AdminLoginPage / AdminLayout).
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    accessToken,
    setUserAuth,
    logout: clearAuthState,
  } = useAuthStore();

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const response = await authService.login(credentials);

      try {
        const profile = await authService.getUserInfo(response.access_token);
        setUserAuth(response.access_token, profile);
      } catch (error) {
        try {
          await authService.logout(response.access_token);
        } catch {
          // Best effort: clear the server refresh cookie when profile loading fails.
        }
        clearAuthState();
        throw error;
      }
    },
    [clearAuthState, setUserAuth],
  );

  const register = useCallback(
    async (data: RegisterRequest) => {
      const response = await authService.register(data);
      setUserAuth(response.access_token, response.user);
    },
    [setUserAuth],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    clearAuthState();
  }, [clearAuthState]);

  const startOAuth = useCallback(
    async (provider: 'google' | 'facebook', redirectTo?: string) => {
      // The backend generates and stores the CSRF state (req 4.4) and the
      // post-login redirect path; the returned URL already carries the state.
      try {
        const result = await authService.getOAuthRedirectUrl(provider, redirectTo);
        window.location.href = result.redirect_url;
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  const loginWithGoogle = useCallback(
    (redirectTo?: string) => startOAuth('google', redirectTo),
    [startOAuth],
  );

  const loginWithFacebook = useCallback(
    (redirectTo?: string) => startOAuth('facebook', redirectTo),
    [startOAuth],
  );

  return {
    user,
    isAuthenticated,
    accessToken,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithFacebook,
  };
}
